import { AnalyticsUserEvent } from '@fruitful/types';
import { useCallback, useEffect, useState } from 'react';
import {
    BodyMediumRegular,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    isNonMobileWeb,
    SegmentedControl,
    Sheet,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { StorageKeys } from '../../../common/constants';
import { useStorage } from '../../../common/providers/StorageProvider';
import { track } from '../../../logger/Logger';
import { type FreeToSpendPeriod } from '../../types/FreeToSpendTypes';

const SPEND_PERIOD_OPTIONS: Array<{ value: FreeToSpendPeriod; label: string }> =
    [
        { value: 'weekly', label: 'Weekly' },
        { value: 'monthly', label: 'Monthly' },
    ];

export interface SpendAccountSettingsSheetProps {
    isOpen: boolean;
    onClose: () => void;
    onPeriodChange?: (period: FreeToSpendPeriod) => void;
    hasMultipleAccounts?: boolean;
}

export const SpendAccountSettingsSheet = ({
    isOpen,
    onClose,
    onPeriodChange,
    hasMultipleAccounts = false,
}: SpendAccountSettingsSheetProps) => {
    const storage = useStorage();
    const [period, setPeriod] = useState<FreeToSpendPeriod>('monthly');
    const [initialPeriod, setInitialPeriod] =
        useState<FreeToSpendPeriod>('monthly');

    useEffect(() => {
        if (!isOpen) return;
        const storedPeriod = storage.getString(StorageKeys.SPEND_WIDGET_PERIOD);
        if (storedPeriod === 'weekly' || storedPeriod === 'monthly') {
            setPeriod(storedPeriod);
            setInitialPeriod(storedPeriod);
        }
    }, [isOpen, storage]);

    const handleSave = useCallback(() => {
        storage.setString(StorageKeys.SPEND_WIDGET_PERIOD, period);
        track(AnalyticsUserEvent.FTS_CHANGE_PERIOD, { period });
        onPeriodChange?.(period);
        onClose();
    }, [storage, period, onPeriodChange, onClose]);

    return (
        <Sheet
            isOpen={isOpen}
            onClose={onClose}
            title="Spend account settings"
            showCloseButton
        >
            <YStack flex={1}>
                <YStack gap={16} marginBottom={24}>
                    <View
                        backgroundColor="$gray200"
                        borderRadius={24}
                        gap={12}
                        paddingVertical={16}
                        paddingHorizontal={16}
                    >
                        <XStack
                            alignItems="center"
                            justifyContent="space-between"
                            gap={16}
                        >
                            <BodyMediumRegular color="$gray1200">
                                Spend period
                            </BodyMediumRegular>
                            <View flex={1}>
                                <SegmentedControl<FreeToSpendPeriod>
                                    selected={period}
                                    defaultValue="monthly"
                                    onValueChange={(val) => {
                                        if (
                                            val === 'weekly' ||
                                            val === 'monthly'
                                        ) {
                                            setPeriod(val);
                                        }
                                    }}
                                    options={SPEND_PERIOD_OPTIONS}
                                    size={isNonMobileWeb ? 'large' : 'small'}
                                />
                            </View>
                        </XStack>
                        <BodySmallRegular color="$gray1000">
                            Choose how your Spend budget is displayed – by week
                            or by month.
                        </BodySmallRegular>
                    </View>

                    {hasMultipleAccounts && (
                        <BodyMediumRegular color="$gray1000" textAlign="center">
                            Settings apply to all Spend accounts
                        </BodyMediumRegular>
                    )}
                </YStack>
                <Button
                    text="Save"
                    variant={ButtonVariant.PRIMARY}
                    size={ButtonSize.LARGE}
                    onPress={handleSave}
                    disabled={period === initialPeriod}
                />
            </YStack>
        </Sheet>
    );
};
